import { useContext } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Office365GroupContext } from "../../context/Office365GroupContext";
import { useOffice365GroupInfo } from "./useOffice365GroupInfo";

const WSU_RED = "#A60F2D";

const chipSx = {
    backgroundColor: "#fdf0f2",
    border: `1px solid ${WSU_RED}`,
    color: WSU_RED,
    fontWeight: 500,
    fontSize: "0.75rem",
    height: "auto",
    maxWidth: "100%",
    "& .MuiChip-label": {
        display: "block",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        py: 0.5,
    },
};

const SECTIONS = [
    {
        heading: "Group Details",
        fields: [
            { label: "Group ID", key: "id", source: "module" },
            { label: "Display Name", key: "displayName" },
            { label: "Description", key: "description" },
        ],
    },
    {
        heading: "Mail Settings",
        fields: [
            { label: "Mail Enabled", key: "mailEnabled" },
            { label: "Mail Alias", key: "mailNickname" },
        ],
    },
    {
        heading: "Privacy & Behavior",
        fields: [
            { label: "Visibility", key: "visibility" },
            { label: "Resource Behavior Options", key: "resourceBehaviorOptions" },
        ],
    },
];

const valueLength = (value) => {
    if (Array.isArray(value)) return value.join(", ").length;
    if (value === null || value === undefined) return 0;
    return String(value).length;
};

const renderCell = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {value.map((v) => (
                    <Chip
                        key={v}
                        label={v}
                        size="small"
                        sx={chipSx}
                    />
                ))}
            </Box>
        ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">None</Typography>
        );
    }

    return value !== undefined && value !== null && value !== "" ? (
        <Typography variant="body2">{value.toString()}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
};

// Returns one "tile" per section (Group Details / Mail Settings / Privacy &
// Behavior), each carrying the length of its longest value.
export const useOffice365GroupInfoSections = () => {
    const { isLoaded, error } = useOffice365GroupInfo();
    const { groupModule } = useContext(Office365GroupContext);

    if (!isLoaded || error) {
        return { isLoaded, error, tiles: [] };
    }

    const group = groupModule?.data;
    if (!group) {
        return { isLoaded, error: null, tiles: [] };
    }

    const tiles = SECTIONS.map((section) => {
        const rows = section.fields.map(({ label, key, source }) => {
            const value = source === "module" ? groupModule[key] : group[key];
            return { key, label, content: renderCell(value), length: valueLength(value) };
        });
        return {
            key: section.heading,
            heading: section.heading,
            rows,
            maxLength: rows.reduce((max, row) => Math.max(max, row.length), 0),
        };
    });

    return { isLoaded, error, tiles };
};
